
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useProductStore } from 'stores/useProductStore'
import { createApiCall } from 'services'
import type { Product } from 'types'
import type { ActivationTriggerValue, ProductContactCard } from '@repo/types'
import { v4 as uuid } from 'uuid'

type UseProductMutationsResult = [
  {
    create: (payload: CreateProductFields) => void
    update: (payload: UpdateProductFields) => void
    setActivationTrigger: (payload: SetProductActivationTriggerFields) => void
    setAccessSetting: (payload: SetProductAccessSettingFields) => void
    remove: (payload: RemoveProductFields) => void
    bulkRemove: (payload: BulkRemoveProductFields) => void
    archive: (payload: ArchiveProductFields) => void
    unarchive: (payload: ArchiveProductFields) => void
  },
  {
    data?: Product
    isLoading: boolean
    isError: boolean
    isSettingActivationTrigger: boolean
    isSettingAccessSetting: boolean
  }
]

/** VRG-528 — `null` clears the override so the product inherits again (AC-6). */
type SetProductActivationTriggerFields = {
  id: string
  activation_trigger: ActivationTriggerValue | null
}

/**
 * VRG-591 — one flag at a time, and `null` CLEARS the override so the product
 * inherits again.
 *
 * PARTIAL by construction: the caller sends the one key it is editing, and the
 * server leaves the other alone. The two flags are independent, so a payload
 * naming both would make an edit to one silently restate the client's possibly
 * stale copy of the other.
 */
type SetProductAccessSettingFields = {
  id: string
} & (
  | {collect_email: boolean | null}
  | {collect_location: boolean | null}
)

type UseProductMutationsOptions = {
  onSuccess?: (data?: any) => void
  onError?: (error?: any) => void
}

type CreateProductFields = {
  type?: string
  title: string
  sku: string
  images: any[]
  description?: string
  details?: string
  quantity: number
  available?: boolean
  manufactured_at: string
  // Link to the listing on the brand's own site. Undeclared until now, so the
  // ProductEditPage payload that has been sending it was an excess-property type
  // error rather than a documented field.
  product_url?: string
  // VRG-323 — per-product scan instructions. Optional: unset means the product
  // falls back to the company-wide default, so omitting them is a real choice
  // rather than an incomplete payload.
  scan_instructions?: string
  scan_media_url?: string
  // VRG-782 — the Experience screen's body for this SKU. Optional in the same sense as
  // the scan fields: absent means this product has no Experience screen, which is a real
  // choice rather than a half-filled payload.
  experience_content?: string
  // VRG-720 — undefined (nothing entered) is omitted; the API validates the shape.
  contact_card?: ProductContactCard
  // VRG-749 — the product's GLB. Optional on CREATE: there is nothing to clear, so
  // omitting it is simply "no model".
  model_url?: string
  // VRG-790 — the reveal pair plus its switch. Optional on CREATE for model_url's
  // reason: there is nothing to clear, so omitting them is simply "no reveal".
  reveal_video_url?: string
  reveal_poster_url?: string
  reveal_enabled?: boolean
}

type UpdateProductFields = {
  id: string
  type?: string
  title?: string
  sku?: string
  images?: any[]
  description?: string
  details?: string
  quantity?: number
  available?: boolean
  manufactured_at?: string
  // `string | null` for the same reason as the two below: clearing the field has to
  // reach the API as an explicit null, since JSON.stringify drops undefined keys.
  product_url?: string | null
  // VRG-323 — see CreateProductFields. `buildFormData` is generic over the payload,
  // so these ride along with no change there.
  //
  // `null` is meaningful on UPDATE and `undefined` is not: buildFormData runs the
  // payload through JSON.stringify, which DROPS undefined keys entirely. So an
  // explicit null is the only way to clear a per-product override and fall back to
  // the company default — omitting the key just leaves the old value in place.
  scan_instructions?: string | null
  scan_media_url?: string | null
  // VRG-782 — `string | null` for the reason given above: buildFormData JSON.stringifies
  // the payload and undefined keys are dropped, so an explicit null is the only way to
  // clear the Experience copy and make the screen disappear again.
  experience_content?: string | null
  // VRG-720 — staff-only. Sent ONLY by a platform_admin; the API refuses (403) any other caller
  // that asks for a change, so a stale copy must never be sent by anyone else.
  claim_on_scan?: boolean
  // VRG-720 — `null` CLEARS the card (undefined would be dropped by JSON.stringify and leave it).
  contact_card?: ProductContactCard | null
  // VRG-749 — `null` is meaningful here for the reason above: it is the only way to
  // REMOVE a model, since an omitted key leaves the old one in place.
  model_url?: string | null
  // VRG-790 — `null` is meaningful on all three for model_url's reason: it is the only
  // way to REMOVE a reveal, since an omitted key leaves the old one in place. The
  // BOOLEAN additionally has a meaningful `false` (turn the reveal off), which is why
  // the form always sends it rather than omitting it when unset.
  reveal_video_url?: string | null
  reveal_poster_url?: string | null
  reveal_enabled?: boolean
}

type RemoveProductFields = { id: string }

type ArchiveProductFields = { id: string }

type BulkRemoveProductFields = {
  ids: string[]
}

// Helper function to build FormData for product mutations
const buildFormData = (payload: any, files: any[]) => {
  const formData = new FormData()

  // Separate new files from existing images
  const newFiles = files.filter((f: any) => !f.isUploaded && f.file)
  const existingImages = files
    .filter((f: any) => f.isUploaded || typeof f === 'string')
    .map((f: any) => (typeof f === 'string' ? f : f.preview_url))

  // Add product data as JSON string
  const productData = { ...payload }
  delete productData.images // Remove images array
  if (existingImages.length) {
    productData.existingImages = existingImages
  }
  formData.append('data', JSON.stringify(productData))

  // Add new image files
  newFiles.forEach((fileObj: any) => {
    formData.append('images', fileObj.file)
  })

  return formData
}

export const useProductMutations = (options?: UseProductMutationsOptions): UseProductMutationsResult => {
  const queryClient = useQueryClient()
  const {
    setLoading,
    setError,
    clearError,
    addProduct,
    updateProduct,
    removeProduct
  } = useProductStore()

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: async (payload: CreateProductFields & { id: string }) => {
      const formData = buildFormData(payload, payload.images || [])
      const response = await createApiCall({
        method: 'POST',
        url: '/products/admin',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: (data) => {
      addProduct(data)
      // refetchType: 'all' so the (inactive) list query refetches now — the
      // products list has refetchOnMount:false, so an invalidate-while-inactive
      // would otherwise leave it showing stale rows when you navigate back to it.
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  /**
   * VRG-528 — its OWN route, deliberately not a field on `update` above.
   *
   * `PUT /products/admin/:id` is gated by `manage_product`, held by every role that
   * can edit a title; activation is the metered, billed event, so the override goes
   * through `POST :id/activation-trigger` on `billing_activation_settings`. Routing
   * it through `update` would slip a billing change past the permission chosen to
   * protect it — and would also drag it through the multipart form-data body, which
   * cannot express an explicit `null`.
   */
  const setActivationTriggerMutation = useMutation({
    mutationFn: async ({
      id,
      activation_trigger,
    }: SetProductActivationTriggerFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/products/admin/${id}/activation-trigger`,
        data: { activation_trigger },
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to set activation trigger')
      }

      return response.data
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: data => {
      updateProduct(data.id, data)
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
    onSettled: () => setLoading(false),
  })

  /**
   * VRG-591 — its OWN route, for the same reason as the activation one above and
   * one more.
   *
   * `PUT /products/admin/:id` is gated by `manage_product`, held by every role that
   * can edit a title; this is a consent decision, so it goes through
   * `POST :id/access-settings` on `certificate_access_settings`. The multipart
   * form-data body that route uses also cannot express an explicit `null`, which is
   * how "stop overriding" is said.
   */
  const setAccessSettingMutation = useMutation({
    mutationFn: async ({id, ...body}: SetProductAccessSettingFields) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/products/admin/${id}/access-settings`,
        data: body,
      })

      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to set access setting')
      }

      return response.data
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: data => {
      updateProduct(data.id, data)
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
    onSettled: () => setLoading(false),
  })

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: UpdateProductFields & { id: string }) => {
      const formData = buildFormData(payload, payload.images || [])
      const response = await createApiCall({
        method: 'PUT',
        url: `/products/admin/${id}`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: (data) => {
      updateProduct(data.id, data)
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  // Delete product mutation
  const deleteMutation = useMutation({
    // Never retry a delete: it's destructive, and a cert-block is a deterministic
    // 400 (retrying just delays the error toast by the global 2s retryDelay).
    retry: false,
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/products/admin/${id}`,
      })
      // createApiCall's interceptor RESOLVES (does not reject) on 4xx — it
      // returns the error response. Without this guard a blocked delete (e.g.
      // 400 when the product has linked certificates) would look like a success
      // and fire onSuccess ("Product has been deleted" + navigate) even though
      // nothing was deleted. Detect the error status and throw so onError fires.
      if (response?.status >= 400) {
        throw { response }
      }
      return id
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: (id) => {
      removeProduct(id)
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(id)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  // Bulk delete products mutation
  const bulkDeleteMutation = useMutation({
    retry: false,
    mutationFn: async (ids: string[]) => {
      // Delete products sequentially to ensure proper error handling
      const results = []
      for (const id of ids) {
        try {
          const res = await createApiCall({
            method: 'DELETE',
            url: `/products/admin/${id}`,
          })
          // 4xx resolves (not rejects) via the interceptor — inspect the status
          // so a blocked delete is recorded as a failure, not a false success.
          if (res?.status >= 400) {
            results.push({ id, success: false, error: res?.data?.message || 'Failed to delete' })
          } else {
            results.push({ id, success: true })
          }
        } catch (error: any) {
          results.push({ id, success: false, error: error?.message || 'Failed to delete' })
        }
      }
      return results
    },
    onMutate: () => {
      setLoading(true)
      clearError()
    },
    onSuccess: (results) => {
      // Remove successfully deleted products from store
      results.forEach(result => {
        if (result.success) {
          removeProduct(result.id)
        }
      })
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(results)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred during bulk deletion')
      options?.onError?.(error)
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  // Archive / unarchive (soft-retire). Always allowed (unlike delete), so a
  // cert-linked product can be hidden from the list without losing its row.
  const archiveMutation = useMutation({
    retry: false,
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/products/admin/${id}/archive`,
      })
      if (response?.status >= 400) {
        throw { response }
      }
      return id
    },
    onSuccess: (id) => {
      // List/detail read from react-query; refetch (all) reflects the change.
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(id)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
  })

  const unarchiveMutation = useMutation({
    retry: false,
    mutationFn: async (id: string) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/products/admin/${id}/unarchive`,
      })
      if (response?.status >= 400) {
        throw { response }
      }
      return id
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['products'], refetchType: 'all' })
      options?.onSuccess?.(id)
    },
    onError: (error: any) => {
      setError(error?.message || 'An error occurred')
      options?.onError?.(error)
    },
  })

  const create = (payload: CreateProductFields) => {
    createMutation.mutate({
      ...payload,
      id: uuid(),
    })
  }

  const update = (payload: UpdateProductFields) => {
    updateMutation.mutate(payload)
  }

  const remove = (payload: RemoveProductFields) => {
    deleteMutation.mutate(payload.id)
  }

  const bulkRemove = (payload: BulkRemoveProductFields) => {
    bulkDeleteMutation.mutate(payload.ids)
  }

  const archive = (payload: ArchiveProductFields) => {
    archiveMutation.mutate(payload.id)
  }

  const unarchive = (payload: ArchiveProductFields) => {
    unarchiveMutation.mutate(payload.id)
  }

  const { isLoading } = useProductStore()

  return [
    {
      create,
      update,
      setActivationTrigger: (payload: SetProductActivationTriggerFields) =>
        setActivationTriggerMutation.mutate(payload),
      setAccessSetting: (payload: SetProductAccessSettingFields) =>
        setAccessSettingMutation.mutate(payload),
      remove,
      bulkRemove,
      archive,
      unarchive,
    },
    {
      data: createMutation.data || updateMutation.data,
      isLoading: isLoading || createMutation.isPending || updateMutation.isPending || setActivationTriggerMutation.isPending || deleteMutation.isPending || bulkDeleteMutation.isPending || archiveMutation.isPending || unarchiveMutation.isPending,
      isError: createMutation.isError || updateMutation.isError || deleteMutation.isError || bulkDeleteMutation.isError,
      isSettingActivationTrigger: setActivationTriggerMutation.isPending,
      isSettingAccessSetting: setAccessSettingMutation.isPending,
    },
  ]
}
